// Core viewer
import { useState } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';

// Plugins
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

// Mesmas constantes do backend (signature.py) para alinhar com a Lacuna
const PAGE_WIDTH_POINTS = 595;
const PAGE_HEIGHT_POINTS = 842;
const POINTS_TO_CM = 0.0352778;
const SIGNATURE_WIDTH_POINTS = 250;
const SIGNATURE_HEIGHT_POINTS = 100;

// Limites (cm) para posição enviada à Lacuna
const LEFT_CM_MIN = 0.5;
const LEFT_CM_MAX = 12.1;
const BOTTOM_CM_MIN = -0.5;
const BOTTOM_CM_MAX = 24;

function PdfComp(props) {

  const { pdfFile, enableSignaturePlacement = false, onSignaturePositionChange } = props;
  const [lastClick, setLastClick] = useState(null);

  // Create new plugin instance
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const handlePageClick = (e, pageIndex, scale, pageWidthPx, pageHeightPx) => {
    if (!enableSignaturePlacement) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xPage = rect.width > 0 ? x * (pageWidthPx / rect.width) : x;
    const yPage = rect.height > 0 ? y * (pageHeightPx / rect.height) : y;

    const boxWidthPx = SIGNATURE_WIDTH_POINTS * (pageWidthPx / PAGE_WIDTH_POINTS);
    const boxHeightPx = SIGNATURE_HEIGHT_POINTS * (pageHeightPx / PAGE_HEIGHT_POINTS);
    const containerLeftPx = xPage - boxWidthPx / 2;
    const containerTopPx = yPage - boxHeightPx / 2;

    const pageWidthCm = PAGE_WIDTH_POINTS * POINTS_TO_CM;
    const pageHeightCm = PAGE_HEIGHT_POINTS * POINTS_TO_CM;
    const widthCm = Math.round(SIGNATURE_WIDTH_POINTS * POINTS_TO_CM * 100) / 100;
    const heightCm = Math.round(SIGNATURE_HEIGHT_POINTS * POINTS_TO_CM * 100) / 100;
    const leftCm = Math.round((containerLeftPx / pageWidthPx) * pageWidthCm * 100) / 100;
    const bottomCm = Math.round(((pageHeightPx - (containerTopPx + boxHeightPx)) / pageHeightPx) * pageHeightCm * 100) / 100;
    const leftCmClamped = Math.max(LEFT_CM_MIN, Math.min(LEFT_CM_MAX, leftCm));
    const bottomCmClamped = Math.max(BOTTOM_CM_MIN, Math.min(BOTTOM_CM_MAX, bottomCm));

    onSignaturePositionChange?.({
      pageNumber: pageIndex + 1,
      left: leftCmClamped,
      bottom: bottomCmClamped,
      width: widthCm,
      height: heightCm,
    });

    setLastClick({ pageIndex, x: xPage, y: yPage });
  };

  return (
    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
    <Viewer
        fileUrl={pdfFile}
        plugins={[defaultLayoutPluginInstance]}
        renderPage={(renderPageProps) => {
          const {
            pageIndex,
            width,
            height,
            scale,
            canvasLayer,
            textLayer,
            annotationLayer,
            markRendered,
          } = renderPageProps;
          const boxWidth = SIGNATURE_WIDTH_POINTS * (width / PAGE_WIDTH_POINTS);
          const boxHeight = SIGNATURE_HEIGHT_POINTS * (height / PAGE_HEIGHT_POINTS);
          const showOverlay = enableSignaturePlacement && lastClick !== null && lastClick.pageIndex === pageIndex;
          const left = showOverlay
            ? Math.max(0, Math.min(lastClick.x - boxWidth / 2, width - boxWidth))
            : 0;
          const top = showOverlay
            ? Math.max(0, Math.min(lastClick.y - boxHeight / 2, height - boxHeight))
            : 0;
          return (
            <div
              style={{ position: 'relative', width, height }}
              onClick={(e) => handlePageClick(e, pageIndex, scale, width, height)}
              role="presentation"
            >
              {canvasLayer.children}
              {textLayer.children}
              {annotationLayer.children}
              {showOverlay && (
                <div
                  style={{
                    position: 'absolute',
                    left,
                    top,
                    width: boxWidth,
                    height: boxHeight,
                    border: '2px dashed #333',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    pointerEvents: 'none',
                  }}
                >
                  Sua assinatura aqui
                </div>
              )}
              {markRendered(pageIndex)}
            </div>
          );
        }}
    />
    </Worker>
  );
}

export default PdfComp;
